import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@apollo/client';
import { useAppDispatch } from '../../../../../redux/hooks';
import { closeModal } from '../../../../../redux/slices/modal';
import KubitModal from '../../../../../components/KubitModal';
import {
  TextButton,
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/Button';
import {
  defaultInvite,
  IInviteUsersModalState,
  IUserInvite,
  userInvitesFormSchema,
  USERS_INVITE_LIMIT,
} from './types';
import { GET_ROLES, USER_INVITE } from '../../../../../utils/apollo/permission';
import {
  IRolesQuery,
  IRolesQueryVariables,
  IUserInviteMutation,
  IUserInviteMutationVariables,
  UserSettingKind,
} from '../../../../../utils/apollo/graphqlSchema';
import * as InviteUsersModalUI from './InviteUsersModalUI';
import InformationalTooltip from '../../../../../components/InformationalTooltip';
import UserInvite from './UserInvite';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import {
  userDefaultSchemaInfo,
  userDefaultViewInfo,
  userRoleInfo,
} from '../../../../../components/KubitTooltip/globalTooltips';
import toastMessage from '../../../../../utils/toastMessage';
import { assertNotNil } from '../../../../../utils/types';
import Syndicate from '../../../../Auth/utils/Syndicate';
import { pluralize } from '../../../../../utils/formatText';
import { useBootstrapSelector } from '../../../../../redux/selectorHooks';
import { formErrorViewed } from '../../../../../utils/analytics/events/error/events';
import { countReactHookFormErrors } from '../../../../../components/Builder/utils/errorUtil';
import { AnalyticsErrorContext } from '../../../../../utils/analytics/events/error/types';
import { useAnalytics } from '../../../../../utils/analytics/useAnalytics';

interface IProps {
  onSubmit: () => void;
}

const InviteUsersModal = ({ onSubmit }: IProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  const { enableSso } = Syndicate.getData();

  const schemaConfig = useBootstrapSelector('schemaConfig');
  assertNotNil(schemaConfig);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const [inviteUsers, { loading }] = useMutation<
    IUserInviteMutation,
    IUserInviteMutationVariables
  >(USER_INVITE);

  const { data } = useQuery<IRolesQuery, IRolesQueryVariables>(GET_ROLES, {
    variables: {
      term: '',
      includeDisabled: false,
      includes: [],
      pagination: { first: 0, after: 0 },
    },
  });
  const roleEntities = data?.roles?.entities ?? [];

  const formMethods = useForm<IInviteUsersModalState>({
    resolver: zodResolver(userInvitesFormSchema),
    defaultValues: {
      users: [defaultInvite(enableSso)],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setError,
  } = formMethods;

  const {
    fields: users,
    append: addUser,
    remove: removeUser,
    replace: replaceUsers,
  } = useFieldArray({
    name: 'users',
    control,
  });

  const handleAddUser = () => {
    addUser(defaultInvite(enableSso));
  };
  const handleRemoveUser = (userIndex: number) => {
    removeUser(userIndex);
  };

  const onSubmitForm = handleSubmit(
    async (formData) => {
      const { users } = formData;

      const response = await inviteUsers({
        variables: {
          input: {
            invites: users.map((user) => {
              if (!user.ssoEnabled) {
                return {
                  email: user.email,
                  lastName: user.lastName,
                  firstName: user.firstName,
                  roleIds: [user.roleId],
                  defaultSchema:
                    user.defaultSchemaId.length > 0
                      ? {
                          kind: UserSettingKind.DefaultSchema,
                          value: user.defaultSchemaId,
                        }
                      : null,
                  defaultView: {
                    kind: UserSettingKind.DefaultView,
                    value: user.defaultView,
                  },
                };
              }

              return {
                email: user.email,
                lastName: '',
                firstName: '',
                roleIds: [user.roleId],
                defaultSchema:
                  user.defaultSchemaId.length > 0
                    ? {
                        kind: UserSettingKind.DefaultSchema,
                        value: user.defaultSchemaId,
                      }
                    : null,
                defaultView: {
                  kind: UserSettingKind.DefaultView,
                  value: user.defaultView,
                },
              };
            }),
          },
        },
      });

      // Check mutation errors
      if (printGraphqlErrors(response?.data?.userInvite?.errors)) {
        return;
      }

      assertNotNil(response?.data?.userInvite?.invites);

      const { successCount, failedInvites } =
        response.data.userInvite.invites.reduce<{
          successCount: number;
          failedInvites: Array<{ user: IUserInvite; error: string }>;
        }>(
          (acc, { user, error }, index) => {
            if (user) {
              acc.successCount++;
            } else if (error) {
              acc.failedInvites.push({ user: users[index], error });
            }

            return acc;
          },
          {
            successCount: 0,
            failedInvites: [],
          },
        );

      if (successCount > 0) {
        toastMessage({
          message: `${successCount} ${pluralize('invitation', successCount)} sent successfully`,
          type: 'info',
        });
      }

      if (failedInvites.length > 0) {
        // Replace users list with failedInvites
        replaceUsers(
          failedInvites.map(({ user, error }, index) => {
            // Assign errors
            setError(`users.${index}.email`, {
              message: error,
              type: 'server',
            });
            return user;
          }),
        );
        track(
          formErrorViewed({
            message: `Failed invites after submit. ${failedInvites.length} errors found.`,
            context: AnalyticsErrorContext.InviteUsers,
          }),
        );
      } else {
        handleClose();
        onSubmit();
      }
    },
    (errors) => {
      track(
        formErrorViewed({
          message: `Failed to invite users. ${countReactHookFormErrors(errors)} errors found.`,
          context: AnalyticsErrorContext.InviteUsers,
        }),
      );
    },
  );

  const allowRemoveUser = users.length > 1;
  const allowAddUsers = users.length < USERS_INVITE_LIMIT;

  return (
    <KubitModal
      open
      onClose={handleClose}
      header={'Invite New Users to Kubit'}
      maxWidth={enableSso ? 644 : 996}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmitForm}>
          <InviteUsersModalUI.FormRow $enableSso={enableSso}>
            {!enableSso && (
              <>
                <InviteUsersModalUI.FormHeader>
                  First Name
                </InviteUsersModalUI.FormHeader>
                <InviteUsersModalUI.FormHeader>
                  Last Name
                </InviteUsersModalUI.FormHeader>
              </>
            )}
            <InviteUsersModalUI.FormHeader>
              Email Address
            </InviteUsersModalUI.FormHeader>
            <InviteUsersModalUI.FormHeader>
              Role
              <InformationalTooltip tooltipOption={userRoleInfo} />
            </InviteUsersModalUI.FormHeader>
            <InviteUsersModalUI.FormHeader>
              Default Schema
              <InformationalTooltip tooltipOption={userDefaultSchemaInfo} />
            </InviteUsersModalUI.FormHeader>
            <InviteUsersModalUI.FormHeader>
              Default View
              <InformationalTooltip tooltipOption={userDefaultViewInfo} />
            </InviteUsersModalUI.FormHeader>
          </InviteUsersModalUI.FormRow>
          <InviteUsersModalUI.FormContainer>
            {users.map((user, userIndex) => (
              <UserInvite
                key={user.id}
                userIndex={userIndex}
                enableSso={enableSso}
                roles={roleEntities}
                onRemove={handleRemoveUser}
                allowRemoveUser={allowRemoveUser}
                schemas={schemaConfig.schemas}
              />
            ))}
            {allowAddUsers && (
              <TextButton onClick={handleAddUser}>+ Add User</TextButton>
            )}
          </InviteUsersModalUI.FormContainer>
          <InviteUsersModalUI.ActionsContainer>
            <SecondaryButton
              type="button"
              onClick={handleClose}
              trackContext="invite users"
              trackAction="cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              isGreyedOut={!isValid || !isDirty}
              disabled={loading}
              trackContext="invite users"
              trackAction="invite"
            >
              Invite
            </PrimaryButton>
          </InviteUsersModalUI.ActionsContainer>
        </form>
      </FormProvider>
    </KubitModal>
  );
};

export default InviteUsersModal;
